import React, { useId } from "react";

/**
 * A small shopfront with a booked calendar beside it — the header of the
 * welcome step's sample booking page.
 *
 * Drawn rather than photographed: the project ships no stock imagery (see
 * FRONTEND-PATTERNS.md, "Images"), and colours come from the theme tokens, so
 * it follows the brand if the palette changes. Purely decorative; the card it
 * sits in is already hidden from assistive technology.
 */
const WelcomeIllustration = () => {
  // Gradient ids are document-global; two copies on one page must not share.
  const id = useId().replace(/:/g, "");
  const sky = `welcome-sky-${id}`;

  // Awning stripes, alternating, and the scalloped edge under them.
  const stripes = [0, 1, 2, 3, 4, 5];

  return (
    <svg
      viewBox="0 0 320 150"
      preserveAspectRatio="xMidYMid slice"
      className="block h-full w-full"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={sky} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--color-brand-50)" />
          <stop offset="1" stopColor="var(--color-brand-100)" />
        </linearGradient>
      </defs>

      <rect width="320" height="150" fill={`url(#${sky})`} />
      <circle cx="286" cy="18" r="46" className="fill-brand-200" opacity="0.45" />
      <circle cx="18" cy="150" r="52" className="fill-brand-200" opacity="0.4" />
      <rect y="128" width="320" height="22" className="fill-brand-200" opacity="0.55" />

      {/* Shopfront */}
      <g transform="translate(58 30)">
        <rect x="0" y="26" width="140" height="72" rx="4" className="fill-white" />
        <rect x="0" y="94" width="140" height="4" className="fill-brand-200" />

        {/* Awning */}
        <path d="M6 0 H134 L144 26 H-4 Z" className="fill-brand-600" />
        {stripes.map((i) =>
          i % 2 === 1 ? (
            <path
              key={`stripe-${i}`}
              d={`M${6 + i * 21.33} 0 H${6 + (i + 1) * 21.33} L${-4 + (i + 1) * 24.67} 26 H${-4 + i * 24.67} Z`}
              className="fill-white"
              opacity="0.9"
            />
          ) : null,
        )}
        {stripes.map((i) => (
          <path
            key={`scallop-${i}`}
            d={`M${-4 + i * 24.67} 26 a12.33 9 0 0 0 24.67 0 Z`}
            className={i % 2 === 1 ? "fill-white" : "fill-brand-600"}
          />
        ))}

        {/* Window with a plant */}
        <rect x="12" y="44" width="62" height="40" rx="3" className="fill-brand-50" />
        <rect x="12" y="44" width="62" height="40" rx="3" fill="none" className="stroke-brand-200" strokeWidth="2" />
        <path d="M30 84 h18 l-3 -12 h-12 Z" className="fill-brand-700" />
        <ellipse cx="33" cy="64" rx="5" ry="10" className="fill-brand-500" transform="rotate(-24 33 64)" />
        <ellipse cx="45" cy="62" rx="5" ry="11" className="fill-brand-400" transform="rotate(22 45 62)" />
        <ellipse cx="39" cy="58" rx="4.5" ry="11" className="fill-brand-600" />

        {/* Door */}
        <rect x="88" y="44" width="38" height="50" rx="3" className="fill-brand-100" />
        <rect x="94" y="50" width="26" height="20" rx="2" className="fill-white" opacity="0.8" />
        <circle cx="119" cy="74" r="2.2" className="fill-brand-700" />
      </g>

      {/* Calendar card, booked */}
      <g transform="translate(222 42)">
        <rect x="0" y="0" width="78" height="72" rx="9" className="fill-white" />
        <rect x="0" y="0" width="78" height="72" rx="9" fill="none" className="stroke-brand-200" strokeWidth="1.5" />
        <path d="M9 0 H69 a9 9 0 0 1 9 9 V18 H0 V9 a9 9 0 0 1 9 -9 Z" className="fill-brand-600" />
        <circle cx="22" cy="9" r="2.5" className="fill-white" />
        <circle cx="56" cy="9" r="2.5" className="fill-white" />
        {[0, 1, 2].map((row) =>
          [0, 1, 2, 3].map((col) => (
            <rect
              key={`${row}-${col}`}
              x={9 + col * 16}
              y={26 + row * 14}
              width="12"
              height="9"
              rx="2"
              className={row === 1 && col === 1 ? "fill-brand-500" : "fill-ink-100"}
            />
          )),
        )}
        <circle cx="70" cy="66" r="12" className="fill-brand-600" />
        <circle cx="70" cy="66" r="12" fill="none" className="stroke-white" strokeWidth="2.5" />
        <path d="M64.5 66 l3.8 3.8 l7 -7.6" fill="none" className="stroke-white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* Foreground leaves */}
      <g transform="translate(24 134)">
        <ellipse cx="0" cy="-12" rx="6" ry="16" className="fill-brand-500" transform="rotate(-28)" />
        <ellipse cx="12" cy="-14" rx="6" ry="18" className="fill-brand-600" transform="rotate(8 12 -14)" />
        <ellipse cx="22" cy="-10" rx="5" ry="13" className="fill-brand-400" transform="rotate(34 22 -10)" />
      </g>
    </svg>
  );
};

export default WelcomeIllustration;
